/**
 * PupilDriveBriefBadge — instructor-facing badge inside Work Mode showing
 * the learner's "How will I drive today?" self-rating + a suggested opening
 * style if they need a gentle start.
 *
 * If the learner hasn't self-rated yet, the instructor sees a "Capture now"
 * row (Q1 instructor fallback). Same options the learner sees.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Heart, Sparkles, Pencil } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function PupilDriveBriefBadge({ pupilId }) {
  const [data, setData] = useState(null);
  const [options, setOptions] = useState([]);
  const [capturing, setCapturing] = useState(false);
  const [saving, setSaving] = useState(false);

  async function load() {
    try {
      const [briefRes, optsRes] = await Promise.all([
        axios.get(`${API}/api/human-performance/pupil/${pupilId}/drive-today`, { withCredentials: true }),
        axios.get(`${API}/api/human-performance/options/drive`, { withCredentials: true }),
      ]);
      setData(briefRes.data || null);
      setOptions(optsRes.data.options || []);
    } catch {
      /* silent */
    }
  }

  useEffect(() => {
    if (!pupilId) return;
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pupilId]);

  async function pick(opt) {
    setSaving(true);
    try {
      const { data: res } = await axios.post(
        `${API}/api/human-performance/pupil/${pupilId}/drive-today`,
        { option_id: opt.id },
        { withCredentials: true }
      );
      setData({ option: res.option, suggested_opening: res.suggested_opening, source: "instructor" });
      setCapturing(false);
      toast.success("Captured — brief updated.");
    } catch {
      toast.error("Couldn't save. Try again.");
    } finally {
      setSaving(false);
    }
  }

  if (!data) return null;

  const has = !!data.option;

  return (
    <div data-testid="pupil-drive-badge" className="pt-5 mt-5 border-t border-slate-800/70">
      <div className="flex items-center gap-2 mb-2">
        <Heart className="w-3.5 h-3.5 text-rose-400" />
        <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">
          How they're arriving
        </p>
      </div>

      <div className="flex items-baseline justify-between gap-3">
        {has ? (
          <p className="text-white text-sm font-bold leading-snug flex items-baseline gap-2">
            <span>{data.option.label}</span>
            <span className="text-[11px] italic text-slate-500 font-normal">
              {data.source === "learner" ? "self-rated" : "captured"}
            </span>
          </p>
        ) : (
          <p className="text-slate-400 text-sm italic">Not captured yet.</p>
        )}
        {has ? (
          <button
            type="button"
            data-testid="pupil-drive-change"
            onClick={() => setCapturing(true)}
            className="inline-flex items-center gap-1 text-[12px] font-semibold text-slate-400 hover:text-white"
          >
            <Pencil className="w-3 h-3" /> Change
          </button>
        ) : (
          <button
            type="button"
            data-testid="pupil-drive-capture"
            onClick={() => setCapturing(true)}
            className="text-[12px] font-semibold text-rose-300 hover:text-rose-200"
          >
            Capture
          </button>
        )}
      </div>

      {has && data.suggested_opening && (
        <p className="mt-2 flex items-start gap-2 text-slate-300 text-sm leading-relaxed">
          <Sparkles className="w-3.5 h-3.5 text-emerald-400 flex-shrink-0 mt-0.5" />
          <span>
            <span className="text-slate-500">Suggested opening: </span>
            {data.suggested_opening}
          </span>
        </p>
      )}

      {capturing && (
        <div className="mt-3">
          <p className="text-[12px] text-slate-500 italic mb-2">Tap whatever fits how they've arrived.</p>
          <div className="flex flex-col gap-1.5">
            {options.map((opt) => (
              <button
                key={opt.id}
                type="button"
                data-testid={`pupil-drive-opt-${opt.id}`}
                disabled={saving}
                onClick={() => pick(opt)}
                className={`text-left text-sm py-1.5 transition-colors ${
                  opt.tone === "ready" ? "text-emerald-300 hover:text-emerald-200" : "text-blue-300 hover:text-blue-200"
                } disabled:opacity-50`}
              >
                · {opt.label}
              </button>
            ))}
            <button
              type="button"
              onClick={() => setCapturing(false)}
              className="mt-1 text-xs text-slate-500 hover:underline self-start"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
